import React, { useState, useEffect } from 'react';
import { 
  X, 
  Sparkles, 
  ArrowRight, 
  ArrowLeft, 
  TrendingUp, 
  Calendar, 
  Receipt, 
  Bot, 
  ShieldCheck, 
  Check, 
  Layers, 
  Database, 
  Sliders, 
  Zap, 
  Play, 
  ExternalLink,
  ChevronRight,
  Target,
  FileText,
  DollarSign,
  Globe
} from 'lucide-react';

export interface InteractiveTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLaunchApp?: () => void;
  onStartTrial?: () => void;
  onLoadDemoData?: () => void;
  onRequestDemo?: () => void;
  onRequestVipDemo?: () => void;
}

// Global Tax Preset Archetypes for international interactive tour preview
const JURISDICTION_PRESETS = [
  {
    code: 'GLOBAL_USD',
    name: 'Global / US (USD $)',
    symbol: '$',
    authority: 'IRS / Global',
    vatLabel: 'Sales Tax / GST',
    vatRate: 0,
    incomeSample: 12000,
    expenseSample: 3800,
    bookingSample: 1500,
    vatAmount: 0,
    corpTaxAmount: 2520, // 21% US Federal / standard hold
    corpTaxLabel: '21% Tax Reserve',
    retainedAmount: 9480,
    notes: 'Configurable fiscal year (Dec 31, Sep 30), multi-currency invoice rendering, zero VAT default.'
  },
  {
    code: 'ZA_ZAR',
    name: 'South Africa (ZAR R)',
    symbol: 'R',
    authority: 'SARS (eFiling)',
    vatLabel: '15% VAT Allocation',
    vatRate: 15,
    incomeSample: 15000,
    expenseSample: 4800,
    bookingSample: 2500,
    vatAmount: 2250,
    corpTaxAmount: 3570, // 28% Provisional Tax Cushion
    corpTaxLabel: '28% Tax Cushion',
    retainedAmount: 9180,
    notes: 'Built-in Feb 28 fiscal year, provisional tax reminders, 15% VAT reserve calculation.'
  },
  {
    code: 'UK_GBP',
    name: 'United Kingdom (GBP £)',
    symbol: '£',
    authority: 'HMRC',
    vatLabel: '20% VAT Allocation',
    vatRate: 20,
    incomeSample: 10000,
    expenseSample: 3200,
    bookingSample: 1200,
    vatAmount: 2000,
    corpTaxAmount: 2000, // 25% Corp Tax
    corpTaxLabel: '25% Corp Tax',
    retainedAmount: 6000,
    notes: 'March 31 fiscal year support, 20% VAT breakdowns, clean export for Making Tax Digital.'
  },
  {
    code: 'EU_EUR',
    name: 'Europe (EUR €)',
    symbol: '€',
    authority: 'EU National Authority',
    vatLabel: 'VAT / TVA / MwSt',
    vatRate: 21,
    incomeSample: 11000,
    expenseSample: 3500,
    bookingSample: 1400,
    vatAmount: 2310,
    corpTaxAmount: 2200,
    corpTaxLabel: 'Corporate Hold',
    retainedAmount: 6490,
    notes: 'Standard Dec 31 fiscal cycle, localized VAT invoicing, IBAN/BIC wire details.'
  }
];

export function InteractiveTourModal({
  isOpen,
  onClose,
  onLaunchApp,
  onStartTrial,
  onLoadDemoData,
  onRequestDemo,
  onRequestVipDemo
}: InteractiveTourModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeJurisdictionIndex, setActiveJurisdictionIndex] = useState(0);
  
  const handleStart = onStartTrial || onLaunchApp || onClose;
  const handleRequest = onRequestVipDemo || onRequestDemo;

  const currentJurisdiction = JURISDICTION_PRESETS[activeJurisdictionIndex];
  
  // Interactive mini-simulator state inside Tour Step 1
  const [interactiveIncome, setInteractiveIncome] = useState(15000);
  const [interactiveExpense, setInteractiveExpense] = useState(4800);
  
  const calculatedMargin = interactiveIncome > 0 ? ((interactiveIncome - interactiveExpense) / interactiveIncome) * 100 : 0;

  // Handle ESC key to exit and lock body scrolling while modal is open
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const tourSteps = [
    {
      id: 'margin_defense',
      title: 'Real-Time Profit Margin Defense',
      eyebrow: 'SUPERPOWER 01',
      icon: TrendingUp,
      accentColor: 'text-teal-400',
      badgeBg: 'bg-teal-500/10 text-teal-300 border-teal-500/25',
      summary: 'Traditional accounting tells you what was spent weeks ago. AzuraOS recalculates your real margin in sub-seconds across any currency.',
      highlights: [
        'Dynamic margin recalculation on every single booking or expense.',
        'Custom safety threshold (e.g., 48%) alerts you before operational costs eat your profit.',
        'Eliminates the #1 cause of first-year SME bankruptcy worldwide: false cash flow illusions.'
      ],
      interactiveType: 'margin_calc'
    },
    {
      id: 'public_booking',
      title: 'Smart Calendar & Client Booking Link',
      eyebrow: 'SUPERPOWER 02',
      icon: Calendar,
      accentColor: 'text-amber-400',
      badgeBg: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
      summary: 'Give your clients a branded booking link (/book) that logs bookings, updates your schedule, and prepares draft invoices automatically.',
      highlights: [
        'Shareable direct booking link for consultations, coaching, and retainer sessions.',
        'Zero manual double-entry: confirmed bookings sync straight to your financial ledger.',
        'Instant Google Calendar export and 1-click status tracking (Pending, Confirmed, Completed).'
      ],
      interactiveType: 'booking_preview'
    },
    {
      id: 'tax_invoicing',
      title: 'Global Tax, Multi-Currency & Fiscal Readiness',
      eyebrow: 'SUPERPOWER 03',
      icon: Globe,
      accentColor: 'text-emerald-400',
      badgeBg: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25',
      summary: 'Whether operating in South Africa (SARS 15% VAT / Feb fiscal year), the US (IRS / $), the UK (HMRC / £), Europe (€), or globally — AzuraOS adapts to your tax authority and local currency.',
      highlights: [
        'Multi-currency support ($, R, £, €, ¥, A$, CA$, CHF, AED) configured with 1 click in Settings.',
        'Configurable fiscal year-end cycles (Feb 28 for SA/Kenya, Mar 31 for UK/India/Canada, Dec 31 for US/EU).',
        'Auto-calculates VAT/GST reserves, corporate tax cushions, and generates professional PDF invoices.',
        'Clean 1-click CSV/JSON export formatted for your local accountant or tax authority eFiling portal.'
      ],
      interactiveType: 'tax_preview'
    },
    {
      id: 'ai_cofounder',
      title: 'Private AI Co-Founder (Strictly BYOK)',
      eyebrow: 'SUPERPOWER 04',
      icon: Bot,
      accentColor: 'text-cyan-400',
      badgeBg: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/25',
      summary: 'Your on-demand strategic CFO powered by Google Gemini. Get pricing advice, runway forecasts, and operational roadmaps with 100% data privacy.',
      highlights: [
        'Bring Your Own Key (BYOK) model — your business financials never train public models.',
        'Context-aware answers tailored specifically to your active streams, margins, and cost drivers.',
        'Pre-configured strategic playbooks for pricing increases, retainer retention, and tax readiness.'
      ],
      interactiveType: 'ai_preview'
    }
  ];

  const currentData = tourSteps[currentStep];
  const Icon = currentData.icon;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Finished tour
      onClose();
      handleStart();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-2 sm:p-4 sm:p-6 overflow-y-auto">
      {/* Clickable Backdrop to easily exit the tour */}
      <div 
        className="fixed inset-0 bg-black/85 backdrop-blur-md transition-opacity animate-fade-in"
        onClick={onClose}
        aria-label="Close tour overlay"
      />

      {/* Modal Card with fixed viewport containment */}
      <div 
        className="relative w-full max-w-3xl max-h-[92vh] max-h-[92dvh] bg-[#151819] border border-[#2A2F31] rounded-3xl shadow-2xl flex flex-col overflow-hidden z-10 animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="interactive-tour-title"
      >
        
        {/* Sticky Header with prominent, easily tap-able close button */}
        <div className="sticky top-0 z-30 bg-[#0C0E0F]/95 backdrop-blur-md px-4 sm:px-6 py-3 sm:py-4 border-b border-[#1F2426] flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-[#14B8A6]/20 border border-[#14B8A6]/30 text-[#14B8A6] flex items-center justify-center shadow-inner shrink-0">
              <Zap size={18} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2">
                <h2 id="interactive-tour-title" className="text-xs sm:text-base font-display font-bold text-white tracking-tight truncate">
                  AzuraOS 60-Second Interactive Tour
                </h2>
                <span className="hidden xs:inline-block px-1.5 py-0.5 text-[8px] sm:text-[9px] font-extrabold uppercase bg-teal-500/20 text-teal-300 rounded border border-teal-500/30 shrink-0">
                  SANDBOX
                </span>
              </div>
              <p className="text-[10px] sm:text-[11px] text-gray-400 truncate">
                Explore the 4 core superpowers that keep solo businesses profitable.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Exit Interactive Tour"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#1E2325] hover:bg-rose-500/20 hover:text-rose-400 text-gray-300 border border-[#2C3438] transition cursor-pointer text-xs font-bold shrink-0 shadow-sm"
          >
            <span className="hidden sm:inline">Exit</span>
            <X size={16} />
          </button>
        </div>

        {/* Step Progression Indicator Bar */}
        <div className="bg-[#111314] px-4 sm:px-6 py-2 border-b border-[#1F2426] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-1.5 sm:gap-2 flex-1 max-w-md">
            {tourSteps.map((step, idx) => (
              <button
                key={step.id}
                type="button"
                onClick={() => setCurrentStep(idx)}
                className={`h-1.5 flex-1 rounded-full transition-all cursor-pointer ${
                  idx === currentStep
                    ? 'bg-[#14B8A6] shadow-sm shadow-[#14B8A6]/50'
                    : idx < currentStep
                    ? 'bg-[#14B8A6]/40'
                    : 'bg-[#2A2F31]'
                }`}
                title={`Jump to ${step.title}`}
              />
            ))}
          </div>
          <span className="text-[10px] font-mono font-bold text-gray-400 pl-3">
            STEP {currentStep + 1} OF {tourSteps.length}
          </span>
        </div>

        {/* Scrollable Tour Slide Content */}
        <div className="p-4 sm:p-7 space-y-5 sm:space-y-6 flex-1 overflow-y-auto overscroll-contain">
          
          {/* Slide Heading */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-mono font-bold uppercase border ${currentData.badgeBg}`}>
                <Icon size={12} />
                <span>{currentData.eyebrow}</span>
              </span>
              <h3 className="text-xl sm:text-2xl font-display font-extrabold text-white tracking-tight">
                {currentData.title}
              </h3>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  if (handleRequest) handleRequest();
                }}
                className="px-3 py-1.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-xl text-xs font-bold border border-gray-700 transition flex items-center gap-1.5 cursor-pointer"
              >
                <Sparkles size={12} className="text-[#14B8A6]" />
                <span>Request VIP 1-on-1</span>
              </button>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-gray-300 leading-relaxed max-w-2xl">
            {currentData.summary}
          </p>

          {/* Interactive Feature Sandbox Box */}
          <div className="bg-[#0C0E0F] border border-[#1F2426] rounded-2xl p-4 sm:p-5 shadow-inner space-y-4">
            
            {/* Step 1: Real-Time Margin Simulator */}
            {currentData.interactiveType === 'margin_calc' && (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <span className="text-[10px] font-mono text-teal-400 font-bold uppercase flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse" />
                    LIVE INTERACTIVE MARGIN CALCULATOR
                  </span>
                  <span className="text-[10px] text-gray-500 font-mono">
                    Drag sliders to test instant margin response
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Controls */}
                  <div className="space-y-3 bg-[#151819] p-3.5 rounded-xl border border-[#2A2F31]">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-bold text-gray-300">
                        <span>Revenue Stream:</span>
                        <span className="text-teal-400 font-mono">R{interactiveIncome.toLocaleString()}</span>
                      </div>
                      <input
                        type="range"
                        min="2000"
                        max="50000"
                        step="1000"
                        value={interactiveIncome}
                        onChange={(e) => setInteractiveIncome(parseInt(e.target.value))}
                        className="w-full accent-[#14B8A6] cursor-pointer"
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between text-xs font-bold text-gray-300">
                        <span>Monthly Expenses:</span>
                        <span className="text-rose-400 font-mono">R{interactiveExpense.toLocaleString()}</span>
                      </div>
                      <input
                        type="range"
                        min="500"
                        max="30000"
                        step="500"
                        value={interactiveExpense}
                        onChange={(e) => setInteractiveExpense(parseInt(e.target.value))}
                        className="w-full accent-rose-500 cursor-pointer"
                      />
                    </div>
                  </div>

                  {/* Visual Output */}
                  <div className="bg-[#151819] p-4 rounded-xl border border-[#2A2F31] flex flex-col justify-between text-center space-y-2">
                    <span className="text-[10px] font-mono uppercase text-gray-400">Live Operating Profit Margin</span>
                    <div className={`text-4xl font-black font-mono tracking-tight ${
                      calculatedMargin < 48 ? 'text-rose-400' : 'text-[#14B8A6]'
                    }`}>
                      {calculatedMargin.toFixed(1)}%
                    </div>
                    <div className={`text-[10px] font-bold px-2 py-0.5 rounded-full inline-block mx-auto ${
                      calculatedMargin < 48 ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'
                    }`}>
                      {calculatedMargin < 48 ? '⚠️ Below 48% Target Safety Threshold' : '🟢 Safe Operating Cushion'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Booking Preview */}
            {currentData.interactiveType === 'booking_preview' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-[10px] font-mono text-amber-400 font-bold uppercase">
                  <span>PUBLIC CLIENT PORTAL (/book)</span>
                  <span className="text-gray-500">Live Simulation</span>
                </div>
                
                <div className="p-4 bg-[#151819] border border-[#2A2F31] rounded-xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="text-xs font-bold text-white">Strategy &amp; Brand Audit (1-on-1)</span>
                      <span className="text-[10px] font-mono px-2 py-0.2 bg-teal-500/10 text-teal-300 rounded border border-teal-500/20">
                        R2,500.00
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-400">
                      Client selects date &amp; time slot. System automatically syncs ledger &amp; generates PDF receipt.
                    </p>
                  </div>
                  <div className="px-3 py-1.5 bg-[#0C0E0F] border border-amber-500/30 text-amber-300 font-mono text-[11px] rounded-lg shrink-0">
                    azuraos.app/book
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Tax Preview */}
            {currentData.interactiveType === 'tax_preview' && (
              <div className="space-y-3.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono text-emerald-400 font-bold uppercase">
                    <Globe size={13} />
                    <span>TAX AUTHORITY &amp; CURRENCY ADAPTER</span>
                  </div>
                  <span className="text-[10px] text-gray-400 font-mono">
                    Click a preset to test local calculations
                  </span>
                </div>

                {/* Jurisdiction Tabs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                  {JURISDICTION_PRESETS.map((preset, pIdx) => {
                    const isSelected = activeJurisdictionIndex === pIdx;
                    return (
                      <button
                        key={preset.code}
                        type="button"
                        onClick={() => setActiveJurisdictionIndex(pIdx)}
                        className={`p-2 rounded-xl text-left border transition-all cursor-pointer ${
                          isSelected
                            ? 'bg-emerald-500/15 border-emerald-400 text-white shadow-sm shadow-emerald-500/10'
                            : 'bg-[#151819] border-[#2A2F31] text-gray-400 hover:text-gray-200 hover:border-gray-600'
                        }`}
                      >
                        <div className="flex items-center justify-between text-[11px] font-bold">
                          <span>{preset.symbol} {preset.name.split(' ')[0]}</span>
                          {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
                        </div>
                        <div className="text-[9px] text-gray-400 truncate">
                          {preset.authority}
                        </div>
                      </button>
                    );
                  })}
                </div>
                
                {/* 3 Calculated Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  <div className="p-3 bg-[#151819] border border-[#2A2F31] rounded-xl">
                    <div className="text-[10px] text-gray-400 uppercase font-mono">{currentJurisdiction.vatLabel}</div>
                    <div className="text-sm font-extrabold text-teal-300 font-mono mt-0.5">
                      {currentJurisdiction.symbol}{currentJurisdiction.vatAmount.toLocaleString()}
                    </div>
                    <div className="text-[9px] text-gray-500">
                      {currentJurisdiction.vatRate > 0 ? `Auto-reserved (${currentJurisdiction.vatRate}%)` : 'Configurable sales tax'}
                    </div>
                  </div>
                  <div className="p-3 bg-[#151819] border border-[#2A2F31] rounded-xl">
                    <div className="text-[10px] text-gray-400 uppercase font-mono">{currentJurisdiction.corpTaxLabel}</div>
                    <div className="text-sm font-extrabold text-emerald-400 font-mono mt-0.5">
                      {currentJurisdiction.symbol}{currentJurisdiction.corpTaxAmount.toLocaleString()}
                    </div>
                    <div className="text-[9px] text-gray-500">{currentJurisdiction.authority} reserve hold</div>
                  </div>
                  <div className="p-3 bg-[#151819] border border-[#2A2F31] rounded-xl">
                    <div className="text-[10px] text-gray-400 uppercase font-mono">True Retained Income</div>
                    <div className="text-sm font-extrabold text-white font-mono mt-0.5">
                      {currentJurisdiction.symbol}{currentJurisdiction.retainedAmount.toLocaleString()}
                    </div>
                    <div className="text-[9px] text-gray-500">Spendable post-tax margin</div>
                  </div>
                </div>

                <div className="p-2.5 bg-[#151819]/80 border border-[#2A2F31] rounded-xl text-[11px] text-gray-300 flex items-center gap-2">
                  <span className="text-emerald-400 shrink-0 font-bold">ℹ️</span>
                  <span><strong>{currentJurisdiction.name}:</strong> {currentJurisdiction.notes}</span>
                </div>
              </div>
            )}

            {/* Step 4: AI Co-Founder Preview */}
            {currentData.interactiveType === 'ai_preview' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-[10px] font-mono text-cyan-400 font-bold uppercase">
                  <span>AI CO-FOUNDER BRIEFING (GEMINI BYOK)</span>
                  <span className="text-gray-500">Private AI</span>
                </div>
                
                <div className="p-3.5 bg-[#151819] border border-cyan-500/30 rounded-xl space-y-2 text-xs">
                  <div className="flex items-center gap-2 text-cyan-300 font-bold">
                    <Bot size={14} />
                    <span>Executive Cash Flow Insight:</span>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-[11px]">
                    "Your Consulting stream yields a 68% margin while Design retainers are currently operating at 34% due to software subscriptions. Recommend bundling subscriptions into retainer fees to protect your target 48% threshold."
                  </p>
                </div>
              </div>
            )}

            {/* Bullet Highlights */}
            <div className="pt-2 border-t border-[#1F2426] space-y-1.5">
              {currentData.highlights.map((point, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs text-gray-300">
                  <div className="w-4 h-4 rounded-full bg-teal-500/10 text-teal-400 border border-teal-500/20 flex items-center justify-center shrink-0 mt-0.5 text-[9px]">
                    ✓
                  </div>
                  <span>{point}</span>
                </div>
              ))}
            </div>

          </div>

        </div>

        {/* Modal Bottom Navigation */}
        <div className="bg-[#0C0E0F]/95 backdrop-blur-md p-3.5 sm:p-5 pb-5 sm:pb-5 border-t border-[#1F2426] flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {onLoadDemoData && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onLoadDemoData();
                }}
                className="px-3 py-2 bg-[#151819] hover:bg-[#1F2426] text-gray-300 hover:text-white rounded-xl text-xs font-bold border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer shadow-sm"
                title="Preload full realistic demo data into the dashboard"
              >
                <Database size={13} className="text-[#14B8A6]" />
                <span>Load Demo Data</span>
              </button>
            )}

            {handleRequest && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  handleRequest();
                }}
                className="px-3 py-2 bg-[#151819] hover:bg-[#1F2426] text-amber-300 hover:text-white rounded-xl text-xs font-bold border border-[#2A2F31] transition flex items-center gap-1.5 cursor-pointer shadow-sm"
                title="Book a live 1-on-1 walkthrough with the founder"
              >
                <Calendar size={13} className="text-amber-400" />
                <span>Request 1-on-1</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="sm:hidden px-3 py-2 bg-[#1A1F21] text-gray-400 hover:text-white rounded-xl text-xs font-medium border border-[#2A2F31] transition cursor-pointer"
            >
              Exit Tour
            </button>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={handlePrev}
                className="px-3.5 sm:px-4 py-2 sm:py-2.5 bg-[#1F2426] hover:bg-[#2A2F31] text-gray-300 hover:text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
              >
                <ArrowLeft size={13} />
                <span>Previous</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleNext}
              className="flex-1 sm:flex-initial px-5 sm:px-6 py-2 sm:py-2.5 bg-[#14B8A6] hover:bg-[#0EA5A4] text-black font-extrabold rounded-xl text-xs shadow-lg shadow-[#14B8A6]/20 transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>{currentStep === tourSteps.length - 1 ? 'Launch Workspace' : 'Next Superpower'}</span>
              <ArrowRight size={14} />
            </button>
          </div>

        </div>

      </div>
    </div>
  );
}
